import {
  defined,
  ScreenSpaceEventType,
  ScreenSpaceEventHandler,
  createGuid,
  CallbackProperty,
  Cartesian3,
  SceneTransforms,
  Cartesian2,
} from "@cesium/engine";
import MeasurementLengthGround from "../measurement/MeasurementLengthGround";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";

/**
 * 지면 거리 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for ground length measurement using mouse/keyboard input.
 *
 * @alias InteractionLengthGround
 * @constructor
 *
 *
 * @param {object} lengthGroundOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [lengthGroundOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [lengthGroundOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [lengthGroundOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {MeasurementLengthGround} [lengthGroundOption.measurementLengthGround] 지면 거리 계산 및 영역 표시 클래스.<br> Ground distance calculation and area display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Ground Length Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementLengthGround = new Ditap.MeasurementLengthGround(viewer);
 *
 * // InteractionLengthGround on/off 실행 시 MeasurementLengthGround 초기화 필수
 * // measurementLengthGround 초기화 방법
 * // 1. InteractionLengthGround 생성 후, MeasurementLengthGround, callback 함수 대입
 * const interactionLengthGround = new Ditap.InteractionLengthGround();
 * interactionLengthGround.measurementLengthGround = measurementLengthGround;
 * interactionLengthGround.startCallback = () => {};
 * interactionLengthGround.addCallback = () => {};
 * interactionLengthGround.endCallback = () => {};
 *
 * // 2. InteractionLengthGround 생성 시, lengthGroundOption 지정
 * const lengthGroundOption = {
 *    measurementLengthGround: measurementLengthGround,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionLengthGround = new Ditap.InteractionLengthGround(lengthGroundOption);
 */
class InteractionLengthGround {
  constructor(lengthGroundOption) {
    if (
      lengthGroundOption &&
      lengthGroundOption.measurementLengthGround instanceof
        MeasurementLengthGround
    ) {
      this.measurementLengthGround = lengthGroundOption.measurementLengthGround;
    }
    this._moveEntities = []; //move 시 생성되는 Entity Id 배열
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._handler = undefined;
    this._isOn = false; // 핸들러 실행 여부
    this._keyDownEvent = undefined;
    this._warningAlert = undefined;
    this._buttonElement = undefined;
    this._polylinePrimitive = undefined;
    this._drawingPointEntities = [];
    this._drawingLabelEntities = [];
    this._drawingPolylineEntities = [];
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    if (lengthGroundOption) {
      if (lengthGroundOption.startCallback instanceof Function) {
        this._startCallback = lengthGroundOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (lengthGroundOption.addCallback instanceof Function) {
        this._addCallback = lengthGroundOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (lengthGroundOption.endCallback instanceof Function) {
        this._endCallback = lengthGroundOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 지면 거리 계산 및 영역 표시 클래스.<br>
   * Ground distance calculation and area display class.
   * @type {MeasurementLengthGround}
   */
  set measurementLengthGround(measurementLengthGround) {
    if (
      measurementLengthGround instanceof MeasurementLengthGround ||
      measurementLengthGround === undefined
    ) {
      this._measurementLengthGround = measurementLengthGround; //원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementLengthGround() {
    return this._measurementLengthGround;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert element.
   * @type {Element}
   */
  set warningAlert(warningAlert) {
    this._warningAlert = warningAlert;
  }
  get warningAlert() {
    return this._warningAlert;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button element that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 동적 폴리라인 primitive.</br>
   * Dynamic polyline primitive
   * @type {Primitive}
   */
  set polylinePrimitive(polylinePrimitive) {
    this._polylinePrimitive = polylinePrimitive;
  }
  get polylinePrimitive() {
    return this._polylinePrimitive;
  }

  /*
   * 지면 거리 측정 중 생성 된 Point Entity 배열. </br>
   * Array of point elements generated during ground length measurements.
   * @type {Entity[]}
   */
  set drawingPointEntities(drawingPointEntities) {
    this._drawingPointEntities = drawingPointEntities;
  }
  get drawingPointEntities() {
    return this._drawingPointEntities;
  }

  /*
   * 지면 거리 측정 중 생성 된 Polyline Entity 배열. </br>
   * Array of polyline elements generated during ground length measurements.
   * @type {Entity[]}
   */
  set drawingPolylineEntities(drawingPolylineEntities) {
    this._drawingPolylineEntities = drawingPolylineEntities;
  }
  get drawingPolylineEntities() {
    return this._drawingPolylineEntities;
  }

  /*
   * 지면 거리 측정 중 생성 된 Label Entity 배열. </br>
   * Arra y of label elements generated during ground length measurements.
   * @type {Entity[]}
   */
  set drawingLabelEntities(drawingLabelEntities) {
    this._drawingLabelEntities = drawingLabelEntities;
  }
  get drawingLabelEntities() {
    return this._drawingLabelEntities;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point.
   * @type {Function}
   */
  set startCallback(startCallback) {
    if (startCallback instanceof Function) {
      this._startCallback = startCallback;
    } else {
      this.startCallback = undefined;
    }
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point.
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this.addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point.
   * @type {Function}
   */
  set endCallback(endCallback) {
    if (endCallback instanceof Function) {
      this._endCallback = endCallback;
    } else {
      this.endCallback = undefined;
    }
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 지면 거리 측정 핸들러 실행. (MeasurementLengthGround 초기화 필수.)</br>
   * Execute ground length measurement handler. (MeasurementLengthGround initialization is required.)
   */
  on() {
    if (this.measurementLengthGround && !this.isOn) {
      this.isOn = true;
      this._isMobile = Utils.detectDeviceType();

      this.measurementLengthGround.viewer.currentEvent = this;
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this.handler = new ScreenSpaceEventHandler(
        this.measurementLengthGround.viewer.canvas
      );

      // 경고창 생성
      this.warningAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "지면 거리 측정 시, ",
        content2: "두 점 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementLengthGround.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭 또는 터치하여",
        "지면거리를 측정할 선을 그려주세요",
        true,
        true
      );

      this.infoLabel = Utils.createInfoLabel(
        this.measurementLengthGround.viewer.container.querySelector(
          ".cesium-viewer"
        ),
        "왼 클릭하여 지면거리를 측정할 선을",
        "그려주세요",
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 지면 거리 측정 핸들러 종료. (MeasurementLengthGround 초기화 필수.)</br>
   * End ground length measurement handler. (MeasurementLengthGround initialization is required.)
   */
  off() {
    if (this.measurementLengthGround && this.isOn) {
      const viewer = this.measurementLengthGround.viewer;

      this.measurementLengthGround.viewer.currentEvent = undefined;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // PRIMITIVE - 다이나믹 폴리라인 프리미티브 제거
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // move 시 생성 되는 entity 제거
      for (const entity of this.moveEntities) {
        viewer.entities.remove(entity);
      }

      this.removeAllDrawingEntities();

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        window.removeEventListener("keydown", this.keyDownEvent);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      this.moveEntities = []; //move 시 생성되는 Entity Id 배열
      this.mousePosition = undefined;
      this.handler = undefined;
      this.isOn = false; // 핸들러 실행 여부
      this.keyDownEvent = undefined;
      this.warningAlert.destroy(); // alert창 삭제
      this.warningAlert = undefined;
      this.polylinePrimitive = undefined;
      this.measurementLengthGround = undefined;
      this.drawingPointEntities = [];
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const viewer = this.measurementLengthGround.viewer;
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            for (const entity of this.moveEntities) {
              viewer.entities.remove(entity);
            }
            this.moveEntities = [];

            if (this.polylinePrimitive) {
              viewer.scene.primitives.remove(this.polylinePrimitive);
            }

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    //measurementLengthGround 삭제
    this.measurementLengthGround?.destroy();
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 지면 거리 측정 후 라벨 생성.</label.clientHeightbr>
   * Generate label after length calculation.
   * @param {Cartesian3[]} clampedCartesians 거리를 계산 할 Cartesian 배열.</br> Cartesian3 array to measure length.
   * @param {Cartesian3} labelPosition 라벨 생성 할 Cartesian 좌표.</br> Where to Create Labels.
   */
  calculateLength(clampedCartesians, labelPosition) {
    const viewer = this.measurementLengthGround.viewer;
    let length = 0;
    for (let i = 0; i < clampedCartesians.length - 1; i++) {
      length =
        length +
        Cartesian3.distance(clampedCartesians[i], clampedCartesians[i + 1]);
    }
    length = Number(length.toFixed(2));
    const totalLength = this.measurementLengthGround.plusLength(length);
    this.measurementLengthGround.length = totalLength;

    const label = Utils.createLabel(viewer, length, totalLength);

    const labelHeight = label.clientHeight;
    this.measurementLengthGround.labelHeight = labelHeight;
    Utils.changeLabelLocation(viewer, labelPosition, label, labelHeight);
    this.drawingLabelEntities.push(label);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.measurementLengthGround.viewer;
    const handler = this.handler;

    handler.setInputAction(async (e) => {
      const position = Utils.pickPosition(e.position, viewer);
      const positions = this.measurementLengthGround.positions;
      this.mousePosition = position;

      let isEqual = false;
      if (positions.length > 0) {
        const lastPosition = positions[positions.length - 1];
        isEqual = Utils.isEqualPosition(lastPosition, position);
      }

      if (defined(position) && !isEqual) {
        const pointOption = {
          viewer: viewer,
          id: createGuid(),
          position: position,
        };
        const point = Utils.createPoint(pointOption);
        this.drawingPointEntities.push(point);
        positions.push(position);

        // 점 생성 시 콜백함수 실행
        if (this.addCallback && this.measurementLengthGround) {
          this.addCallback(this.measurementLengthGround);
        }

        if (positions.length > 1) {
          const array = [];
          array.push(positions[positions.length - 2]);
          array.push(positions[positions.length - 1]);

          const polylineOption = {
            viewer: viewer,
            id: createGuid(),
            positions: array,
          };
          const polyline = Utils.createPolyline(polylineOption);
          this.drawingPolylineEntities.push(polyline);
        } else if (
          positions.length === 1 &&
          this.startCallback &&
          this.measurementLengthGround
        ) {
          // 첫번째 점 생성 시 콜백함수 실행
          this.startCallback(this.measurementLengthGround);
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementLengthGround.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);
      const positions = this.measurementLengthGround.positions;

      if (defined(position)) {
        this.mousePosition = position;

        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );

        // move 시 생성 된 entities 제거
        for (const entity of this.moveEntities) {
          viewer.entities.remove(entity);
        }
        this.moveEntities = [];

        if (positions.length > 0) {
          const polylineOption = {
            viewer: viewer,
            id: createGuid(),
            positions: new CallbackProperty(() => {
              const left = positions[positions.length - 1];
              const right = position;

              const leftCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                left
              );
              const rightCart = SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene,
                right
              );

              const distance = Cartesian2.distance(leftCart, rightCart);

              const resultArray = [left];
              if (distance < 100) {
                resultArray.push(left);
              } else {
                const middle = Cartesian3.lerp(
                  left,
                  right,
                  9 / 10,
                  new Cartesian3()
                );
                resultArray.push(middle);
              }

              return resultArray;
            }, false),
          };

          const polyline = Utils.createPolyline(polylineOption);
          this.moveEntities.push(polyline);
        }
        if (this.polylinePrimitive) {
          viewer.scene.primitives.remove(this.polylinePrimitive);
        }

        if (positions.length > 0) {
          const array = [];
          array.push(positions[positions.length - 1]);
          array.push(position);
          const primitiveOption = {
            viewer: viewer,
            positions: array,
          };
          const polyline = Utils.createPolylinePrimitive(primitiveOption);
          this.polylinePrimitive = polyline;
        }
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 더블 클릭 시 실행 이벤트 생성. </br>
   * Create an event for executing actions when the mouse is double-clicked.
   */
  createDoubleClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 지면 거리 측정 종료.<br>
   * End ground length measurement.
   */
  endHandler() {
    const viewer = this.measurementLengthGround.viewer;
    const positions = this.measurementLengthGround.positions;
    if (positions.length > 1) {
      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      const polylineOption = {
        viewer: viewer,
        id: createGuid(),
        positions: positions,
      };
      const polyline = Utils.createPolyline(polylineOption);
      this.drawingPolylineEntities.push(polyline);

      // 마지막 점 생성 시 콜백함수 실행
      if (this.endCallback && this.measurementLengthGround) {
        this.endCallback(this.measurementLengthGround);
      }

      this.measurementLengthGround.createEntity(() => {
        this.off();
      });
    } else {
      this.warningAlert.open();
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = this.mousePosition;

      if (defined(position)) {
        const positions = this.measurementLengthGround.positions;

        // 두 점 이상일 때 > 마지막 Entity 제거, 배열에서도 제거
        if (positions.length > 0) {
          this.removePoint();
        } else {
          this.destroy();
        }
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료
      this.destroy();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    const viewer = this.measurementLengthGround.viewer;
    const positions = this.measurementLengthGround.positions;
    const pointEntities = this.drawingPointEntities;
    const polylineEntities = this.drawingPolylineEntities;
    const moveEntities = this.moveEntities;

    // 두 점 이상일 때 > 마지막 Entity 제거, 배열에서도 제거
    if (positions.length > 0) {
      const point = pointEntities[pointEntities.length - 1];
      // 마지막 point 제거
      this.measurementLengthGround.removeLastElement(pointEntities);
      this.measurementLengthGround.removeViewerEntity(point);

      for (const entity of moveEntities) {
        viewer.entities.remove(entity);
      }

      if (positions.length > 1) {
        // 마지막 polyline 제거
        const polyline = polylineEntities[polylineEntities.length - 1];
        this.measurementLengthGround.removeLastElement(polylineEntities);
        this.measurementLengthGround.removeViewerEntity(polyline);
      }

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      positions.pop();

      if (!this._isMobile && positions.length > 0) {
        const array = [];
        array.push(positions[positions.length - 1]);
        array.push(this.mousePosition);

        const polylineOption = {
          viewer: viewer,
          id: createGuid(),
          positions: new CallbackProperty(() => array, false),
        };
        const polyline = Utils.createPolyline(polylineOption);
        this.moveEntities.push(polyline);
      }
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /*
   * 측정 중 생성된 모든 Point Entity 제거.</br>
   * Remove all point entities created during measurement and initialize arrays.
   */
  removeDrawingPointEntities() {
    for (const entity of this.drawingPointEntities) {
      this.measurementLengthGround.viewer.entities.remove(entity);
    }
    this.drawingPointEntities = [];
  }

  /*
   * 측정 중 생성된 모든 Polyline Entity 제거.</br>
   * Remove all polyline entities created during measurement and initialize arrays.
   */
  removeDrawingPolylineEntities() {
    for (const entity of this.drawingPolylineEntities) {
      this.measurementLengthGround.viewer.entities.remove(entity);
    }
    this.drawingPolylineEntities = [];
  }

  /*
   * 측정 중 생성된 모든 Label Entity 제거.</br>
   * Remove all label entities created during measurement and initialize arrays.
   */
  removeDrawingLabelEntities() {
    for (const label of this.drawingLabelEntities) {
      label.remove();
    }
    this.drawingLabelEntities = [];
  }

  /*
   * 측정 중 생성된 모든 엔티티 제거.</br>
   * Remove all entities created during measurement and initialize arrays.
   */
  removeAllDrawingEntities() {
    this.removeDrawingPointEntities();
    this.removeDrawingPolylineEntities();
    this.removeDrawingLabelEntities();
  }
}

export default InteractionLengthGround;
